import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { CrossmatchListComponent } from './crossmatch-list/crossmatch-list.component';
import { AddCrosmatchDialogResult, AddCrossmatchComponent, AddCrossmatchDialogData } from './add-crossmatch/add-crossmatch.component';
import { Observable } from 'rxjs';
import { Crossmatch } from '../../interfaces/crossmatch';
import { MatDialog } from '@angular/material/dialog';
import { CrossmatchService } from '../../services/crossmatch.service';
import { PairService } from '../../services/pair.service';
import { NotifyService } from '../../common/notify/notify.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../auth/auth.service';
import { Permission } from '../../interfaces/permission';

type FilterType = 'current' | 'historical' | 'all';

@Component({
  selector: 'app-crossmatch',
  imports: [PagetoolbarComponent, MatCardModule, MatIconModule, MatToolbarModule, MatTabsModule, MatButtonToggleModule, CrossmatchListComponent, CommonModule],
  providers: [NotifyService],
  templateUrl: './crossmatch.component.html',
  styleUrl: './crossmatch.component.scss'
})
export class CrossmatchComponent {
  @ViewChild(MatTabGroup, { static: true }) tabgroup!: MatTabGroup;

  public positiveCrossmatches?: Crossmatch[] = [];
  public negativeCrossmatches?: Crossmatch[] = [];
  public filteredPositiveCrossmatches?: Crossmatch[] = [];
  public filteredNegativeCrossmatches?: Crossmatch[] = [];

  public ecCount: number = 0;
  public pcCount: number = 0;
  public ncCount: number = 0;

  public selectedFilter: FilterType = 'current';

  private activeTab: number = 0;
  private searchFieldValue$!: Observable<string>;
  addCrossmatches: boolean = false;
  constructor(
    private readonly dialog: MatDialog,
    private readonly crossmatchService: CrossmatchService,
    private readonly pairService: PairService,
    private readonly authService: AuthService,
    private readonly notify: NotifyService
  ) {}

  ngOnInit(): void {
    this.authService.getAllPermissions$().subscribe((permissions) => {
      this.addCrossmatches = permissions.includes(Permission.CROSSMATCH_RESULT_ADD);
    });
    this.loadCrossmatches();

    // Subscribe to service state changes and re-apply filter
    this.crossmatchService.positiveCrossmatches$.subscribe((crossmatches) => {
      this.positiveCrossmatches = crossmatches;
      this.applyFilter();
    });

    this.crossmatchService.negativeCrossmatches$.subscribe((crossmatches) => {
      this.negativeCrossmatches = crossmatches;
      this.applyFilter();
    });
  }

  private loadCrossmatches(): void {
    this.crossmatchService.getAllCrossmatches().subscribe({
      next: (data: Crossmatch[]) => {
        const positive = data.filter((c) => c.positive === true);
        const negative = data.filter((c) => c.positive === false);
        this.crossmatchService.setNegativeCrossmatches(negative);
        this.crossmatchService.setPositiveCrossmatches(positive);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public onFilterChange(filter: FilterType): void {
    this.selectedFilter = filter;
    this.applyFilter();
  }

  private applyFilter(): void {
    this.filteredPositiveCrossmatches = this.filterCrossmatches(this.positiveCrossmatches || []);
    this.filteredNegativeCrossmatches = this.filterCrossmatches(this.negativeCrossmatches || []);
  }

  private filterCrossmatches(crossmatches: Crossmatch[]): Crossmatch[] {
    if (this.selectedFilter === 'all') {
      return crossmatches;
    }

    return crossmatches.filter((crossmatch) => {
      const donorHistorical = crossmatch.donor?.historical === true;
      const recipientHistorical = crossmatch.recipient?.historical === true;
      const donorMissing = !crossmatch.donor;
      const recipientMissing = !crossmatch.recipient;

      const isHistorical = donorHistorical || recipientHistorical || donorMissing || recipientMissing;

      if (this.selectedFilter === 'current') {
        return !isHistorical;
      } else if (this.selectedFilter === 'historical') {
        return isHistorical;
      }

      return true;
    });
  }
  openAddDialog() {
    const highlightRow = (data?: { donorId: string; recipientId: string }) => {
      if (!data) {
        return;
      }
      const newRow = document.getElementById(`${data.recipientId}|${data.donorId}`);
      if (newRow) {
        newRow.classList.add('highlight');
        this.openSnackBar('Added new excluded Recipient/Donor combination');
      }
    };

    if (this.tabgroup.selectedIndex === 0) {
      this.openCrossmatchDialog({ type: 'Positive' });
    } else if (this.tabgroup.selectedIndex === 1) {
      this.openCrossmatchDialog({ type: 'Negative' });
    }
  }

  private openCrossmatchDialog(data: AddCrossmatchDialogData) {
    const dialogRef = this.dialog.open<AddCrossmatchComponent>(AddCrossmatchComponent, { data });
    dialogRef.afterClosed().subscribe((result) => {
      console.log(result);
      if (result) {
        if (!result.positive) {
          this.crossmatchService.addNegativeCrossmatch(result);
        } else if (result.positive) {
          this.crossmatchService.addPositiveCrossmatch(result);
        }
      }
    });
  }

  key(_index: number, crossmatch: Crossmatch) {
    return crossmatch.id;
  }

  openSnackBar(message: string, action?: string) {
    this.notify.withAutodismiss(message, action);
  }
}
